import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DevActorPreset } from './api.ts';

interface ActorState {
  actor: DevActorPreset;
  // P2: 職級 rank_level（公告建立等 rank-gate 用）。dev 由 preset 推；正式登入
  // 時 LoginPage 用後端 title_rank 覆寫成真值。
  titleRank: number;
  setActor: (a: DevActorPreset) => void;
  setTitleRank: (r: number) => void;
}

export const useActor = create<ActorState>()(
  persist(
    (set) => ({
      actor: 'admin',
      titleRank: 100,
      setActor: (actor) => set({ actor, titleRank: actorTitleRank(actor) }),
      setTitleRank: (titleRank) => set({ titleRank }),
    }),
    {
      name: 'sanshi-dev-actor',
      version: 1,
    },
  ),
);

// Identity helpers — the ActorSwitcher's preset string is the only thing
// the SPA knows about role. We map that to the same buckets the backend's
// RLS uses (admin / member / lp) so UI gating decisions match what the
// DB will enforce. If the two disagree, the DB wins (it always does —
// these helpers just avoid showing buttons that would 403 on click).

export type ActorRole = 'admin' | 'member' | 'lp';

export function actorRole(actor: DevActorPreset): ActorRole {
  if (typeof actor === 'object' && 'lp' in actor) return 'lp';
  // 2026-05-11: 'samson' named preset 也是 admin (對應 dev-actor.ts user_id=1)
  // P1: 'partner' = 業務 admin（Partner 職級，看全部）但非系統管理員。
  if (actor === 'admin' || actor === 'admin:t2' || actor === 'samson' || actor === 'partner') {
    return 'admin';
  }
  return 'member';
}

/** P1 (RFC §10): 系統管理員能力（帳號/字典/工具/稽核 sidebar+動作 gate）。跟
 *  actorRole（看全部）分離 — 'partner'（Partner 職級）isAdmin=true 但這裡 false。
 *  正式登入時 LoginPage 依後端 is_sysadmin 把 preset 設成 'admin' 或 'partner'。 */
export function actorIsSysadmin(actor: DevActorPreset): boolean {
  return actor === 'admin' || actor === 'admin:t2' || actor === 'samson';
}

/** P2: preset → 職級 rank_level（dev fallback；正式登入由後端 title_rank 覆寫）。
 *  對應 dev-actor.ts / 6 位成員的職級。 */
export function actorTitleRank(actor: DevActorPreset): number {
  if (typeof actor === 'object') return 0; // lp
  switch (actor) {
    case 'admin':
    case 'samson':
    case 'admin:t2':
      return 100; // Managing Partner
    case 'partner':
    case 'andy':
      return 90; // Partner
    case 'member':
    case 'brandon':
    case 'fund_manager':
    case 'shannon':
      return 70; // Director / 總監 (前稱 Principal/董事;rank 70 不變)
    case 'fund_member':
    case 'allen':
      return 60; // VP
    default:
      return 0;
  }
}

/** P2: 目前 actor 的職級 rank（公告建立 gate 等用；Director/總監=70）。 */
export function useTitleRank(): number {
  return useActor((s) => s.titleRank);
}

/** Convenience — `useActor(s => actorRole(s.actor) === 'admin')`. */
export function useIsAdmin(): boolean {
  return useActor((s) => actorRole(s.actor) === 'admin');
}

/** True when the actor holds the 系統管理員 capability. */
export function useIsSysadmin(): boolean {
  return useActor((s) => actorIsSysadmin(s.actor));
}

export function actorLabel(actor: DevActorPreset): string {
  if (typeof actor === 'object' && 'lp' in actor) return `LP #${actor.lp}`;
  // V151 (2026-06-14): include 中文名 alongside English short name for the
  // dev-actor preset switcher (only loosely tied to app_user.name_zh — these
  // labels are hard-coded since the switcher is a dev tool, not a query).
  switch (actor) {
    case 'admin':
    case 'samson':
      return 'Samson（姚舜晏）· admin';
    case 'admin:t2':
      return 'Bob · admin (tenant 2)';
    case 'partner':
      return 'Andy（施宣宇）· Partner（業務 admin）';
    case 'member':
    case 'brandon':
      return 'Brandon（黃子庭）· member';
    case 'fund_member':
    case 'allen':
      return 'Allen（林子倫）· member';
    case 'fund_manager':
    case 'shannon':
      return 'Shannon（洪筠）· member';
    case 'andy':
      return 'Andy（施宣宇）· member';
    default:
      return String(actor);
  }
}
